import { alpha, createTheme, type ThemeOptions } from '@mui/material/styles';
import { Skin, ThemeOption } from '../types/enums';
import { getSkinComponentOverrides, getThemeDefinition } from './themeConfig';
import { defaultScrollbar } from './scrollbarStyles';
import { alertComponents } from './components/alerts';

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    secondary: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body2Compact: true;
    body3: true;
    body4: true;
    caption2: true;
    subtitle4: true;
  }
}

export const createCustomTheme = (skin: Skin, theme: ThemeOption) => {
  const resolvedTheme = getThemeDefinition(skin, theme);
  const baseComponents: ThemeOptions['components'] = {
    ...alertComponents,
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          body3Medium: 'p',
          body2Compact: 'p',
          body3: 'p',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: defaultScrollbar(resolvedTheme.palette),
        input: resolvedTheme.input,
      },
    },
    MuiButton: {
      variants: [
        {
          props: { variant: 'secondary' },
          style: ({ theme }) => {
            const { borderRadius } = theme.shape;

            return {
              ...theme.typography.body2,
              height: 48,
              minHeight: 48,
              padding: theme.spacing(1.25, 2),
              borderRadius: typeof borderRadius === 'number' ? borderRadius * 2 : `calc(${borderRadius} * 2)`,
              border: 0,
              backgroundColor: theme.palette.neutral[600],
              color: theme.palette.neutral[25],
              fontWeight: 700,
              '&:hover': {
                backgroundColor: theme.palette.neutral[500],
              },
              '&.Mui-focusVisible': {
                outline: `2px solid ${theme.palette.secondary.main}`,
                outlineOffset: 2,
              },
              '&.Mui-disabled': {
                color: theme.palette.neutral[400],
                backgroundColor: theme.palette.neutral[600],
              },
            };
          },
        },
      ],
      styleOverrides: {
        root: {
          borderRadius: 20,
          height: 40,
          fontSize: 14,
          fontWeight: 600,
          lineHeight: '20px',
          padding: '10px 24px',
          textTransform: 'none',
          boxShadow: 'none',
          ':hover': {
            boxShadow: 'none',
          },
        },
      },
      defaultProps: {
        variant: 'contained',
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          input: {
            padding: '16px',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: resolvedTheme.palette.neutral?.[700],
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          color: 'neutral.25',
          '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
          },
          MozAppearance: 'textfield',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#A0A0A0',
          '&.Mui-focused': {
            color: resolvedTheme.palette.inputStyles?.focus,
          },
          '&.Mui-error': {
            color: '#f44336',
          },
          '&.Mui-error.Mui-focused': {
            color: '#f44336',
          },
          '&.Mui-disabled': {
            color: resolvedTheme.palette.neutral?.[300],
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(resolvedTheme.palette.neutral?.[600] || '#000', 0.25),
          '& fieldset': {
            borderColor: '#808080',
          },
          '&:hover fieldset': {
            borderColor: '#909090',
          },
          '&.Mui-focused fieldset': {
            borderColor: resolvedTheme.palette.inputStyles?.focus,
          },
          '&.Mui-error.Mui-focused fieldset': {
            borderColor: '#f44336',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: resolvedTheme.palette.inputStyles?.focus,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: '#909090',
          '&.Mui-error': {
            color: '#f44336',
          },
        },
      },
    },
  };

  const mergeThemeComponents = (
    base: ThemeOptions['components'],
    overrides?: ThemeOptions['components']
  ): ThemeOptions['components'] => {
    if (!overrides) {
      return base;
    }

    const mergedOverrides = Object.fromEntries(
      (
        Object.entries(overrides) as Array<
          [
            keyof NonNullable<ThemeOptions['components']>,
            NonNullable<ThemeOptions['components']>[keyof NonNullable<ThemeOptions['components']>],
          ]
        >
      )
        .filter(([, overrideConfig]) => Boolean(overrideConfig))
        .map(([key, overrideConfig]) => {
          const baseConfig = base?.[key] as
            | {
                defaultProps?: object;
                styleOverrides?: object;
                variants?: object[];
              }
            | undefined;

          const typedOverride = overrideConfig as {
            defaultProps?: object;
            styleOverrides?: object;
            variants?: object[];
          };

          return [
            key,
            {
              ...baseConfig,
              ...typedOverride,
              defaultProps: {
                ...(baseConfig?.defaultProps ?? {}),
                ...(typedOverride.defaultProps ?? {}),
              },
              styleOverrides: {
                ...(baseConfig?.styleOverrides ?? {}),
                ...(typedOverride.styleOverrides ?? {}),
              },
              variants: [...(baseConfig?.variants ?? []), ...(typedOverride.variants ?? [])],
            },
          ];
        })
    ) as ThemeOptions['components'];

    return {
      ...base,
      ...mergedOverrides,
    };
  };

  return createTheme({
    palette: resolvedTheme.palette,
    typography: resolvedTheme.typography,
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1600,
      },
    },
    components: mergeThemeComponents(
      mergeThemeComponents(baseComponents, getSkinComponentOverrides(skin, resolvedTheme.palette)),
      resolvedTheme.components
    ),
  });
};
